import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AssetUrls } from 'config/assets';
import { showError, showSuccess } from 'utils/toastNotifications';
import { useVerifyEmailQuery, useResendVerificationMutation } from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { trackEvent, getUserProperties } from 'utils/analytics';
import './AccountVerificationPage.css';

const AccountVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const hasTrackedEvent = useRef(false);

  // Resend verification state
  const [email, setEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  const { data: verificationData, isLoading, error } = useVerifyEmailQuery(token || '', {
    skip: !token,
  });

  const [resendVerification, { isLoading: isResending }] = useResendVerificationMutation();

  // Fetch user data for analytics (skip if not authenticated or verification failed)
  const { data: userData } = useGetMeQuery(undefined, {
    skip: !verificationData?.message,
  });

  // Track email verification in GA4 (only once)
  useEffect(() => {
    if (verificationData?.message && userData && !hasTrackedEvent.current) {
      hasTrackedEvent.current = true;
      trackEvent('email_verified', getUserProperties({
        id: userData.id,
        createdAt: userData.createdAt,
        country: userData.country,
      }));
    }
  }, [verificationData, userData]);

  // Handle successful verification
  useEffect(() => {
    if (verificationData?.message) {
      showSuccess(verificationData.message);

      // Start countdown for redirect
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/home');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [verificationData, navigate]);

  // Handle error cases - show toast but don't auto-redirect (user may want to resend)
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || 'This verification link is invalid or has expired.';
      showError(errorMessage);
    }
  }, [error]);

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitSeconds > 0) {
      const timer = setTimeout(() => {
        setRateLimitSeconds(rateLimitSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitSeconds]);

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Format time for rate limit display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  // Handle resend verification email
  const handleResendEmail = async () => {
    if (!email || !isValidEmail(email) || isResending || rateLimitSeconds > 0) {
      return;
    }

    // Clear previous messages
    setResendSuccess(false);
    setResendError(null);

    try {
      await resendVerification({ email }).unwrap();
      setResendSuccess(true);
      setEmail(''); // Clear input on success
    } catch (err: any) {
      console.error('Error resending verification:', err);

      if (err?.status === 429) {
        // Rate limited - parse retry time from message if available
        const message = err?.data?.message || '';
        const minutesMatch = message.match(/(\d+)\s+minute/i);

        if (minutesMatch) {
          const minutes = parseInt(minutesMatch[1], 10);
          setRateLimitSeconds(minutes * 60);
          setResendError(`Too many requests. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`);
        } else {
          // Default to 60 minutes for rate limit
          setRateLimitSeconds(3600);
          setResendError('Too many requests. Please try again later.');
        }
      } else if (err?.data?.message) {
        setResendError(err.data.message);
      } else {
        setResendError('Failed to send verification email. Please try again.');
      }
    }
  };

  const isResendDisabled = !email || !isValidEmail(email) || isResending || rateLimitSeconds > 0;

  if (isLoading) {
    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-loading">
            <div className="account-verification-spinner"></div>
            <p>Verifying your email...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = (error as any)?.data?.message || 'This verification link is invalid or has expired.';

    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-logo">
            <img src={AssetUrls.logos.iconLogo} alt="TradeMagic" />
          </div>

          <div className="account-verification-error">
            <div className="error-icon">✕</div>
            <h1 className="account-verification-title">Verification Failed</h1>
            <p className="error-message">
              {errorMessage}
            </p>

            {/* Resend Verification Section */}
            <div className="resend-section">
              <p className="resend-label">
                Enter your email to receive a new verification link:
              </p>
              <input
                type="email"
                className="resend-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setResendError(null); // Clear error when typing
                }}
                disabled={isResending || rateLimitSeconds > 0}
              />
              <button
                className="resend-button"
                onClick={handleResendEmail}
                disabled={isResendDisabled}
              >
                {isResending ? 'Sending...' : 'Resend Verification Email'}
              </button>

              {rateLimitSeconds > 0 && (
                <p className="rate-limit-timer">
                  Try again in {formatTime(rateLimitSeconds)}
                </p>
              )}

              {resendSuccess && (
                <p className="resend-success">
                  If an account exists with this email, a verification link has been sent. Please check your inbox.
                </p>
              )}

              {resendError && (
                <p className="resend-error">
                  {resendError}
                </p>
              )}
            </div>

            <p className="error-submessage">
              Already verified? <a href="/welcome" className="inline-link">Log in here</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationData) {
    return (
      <div className="account-verification-page">
        <div className="account-verification-container">
          <div className="account-verification-logo">
            <img src={AssetUrls.logos.iconLogo} alt="TradeMagic" />
          </div>

          <div className="account-verification-success">
            <div className="success-icon">✓</div>
            <h1 className="account-verification-title">Email Verified!</h1>

            <p className="success-message">
              Your email address has been successfully verified.
            </p>

            {verificationData.email && (
              <p className="verified-email">
                <strong>{verificationData.email}</strong>
              </p>
            )}

            <p className="redirect-message">
              Redirecting to your dashboard in {countdown} seconds...
            </p>

            <p className="info-message">
              You can now send and receive cards on TradeMagic.
            </p>

            <a href="/home" className="account-verification-link">
              Go to Dashboard Now
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AccountVerificationPage;